/// <reference lib="webworker" />

import { getAccessToken } from "@opendonationassistant/recipient-service";
import { Event, EventBus, Variable } from "../../../bus/EventBus";
import { uuidv7 } from "uuidv7";
import { EmotesStore } from "../../../stores/EmotesStore";
import { reportError, reportStarted } from "../worker-status";
import { emotesFromText } from "./emotes";
import type { TokenDto } from "../systems";
import { log } from "./log";

const VKLIVE_WEBSOCKET_URL =
  "wss://pubsub-dev.live.vkvideo.ru/connection/websocket?cf_protocol_version=v2";
const VKLIVE_API_URL = "https://apidev.live.vkvideo.ru";
const RECONNECT_DELAY_MS = 1000;
const API_TIMEOUT_MS = 15000;

const EVENT_NAME = "VKLIVE_CHAT_MESSAGE";

async function getJson(url: string, token: string): Promise<any> {
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + token,
    },
    signal: AbortSignal.timeout(API_TIMEOUT_MS),
  });
  if (!response.ok) {
    log("ERROR",
      "VK Live API call failed with status code " +
        response.status +
        ": " +
        url,
    );
  }
  return response.json();
}

async function getChatChannelName(token: string): Promise<string> {
  const channelUrl = (await getJson(`${VKLIVE_API_URL}/v1/current_user`, token))
    .data.channel.url as string;
  const json = await getJson(
    `${VKLIVE_API_URL}/v1/channel?channel_url=${encodeURIComponent(channelUrl)}`,
    token,
  );
  return json.data.channel.web_socket_channels.chat;
}

async function getConnectionToken(token: string): Promise<string> {
  const json = await getJson(`${VKLIVE_API_URL}/v1/websocket/token`, token);
  return json.data.token;
}

async function getSubscriptionToken(
  token: string,
  channel: string,
): Promise<string> {
  const json = await getJson(
    `${VKLIVE_API_URL}/v1/websocket/subscription_token?channels=${encodeURIComponent(channel)}`,
    token,
  );
  const entry = json.data.channel_tokens.find(
    (it: { channel: string }) => it.channel === channel,
  );
  return entry?.token ?? "";
}

function messageText(message: any): string {
  const parts: any[] = message.parts ?? [];
  return parts
    .map((part) => part.text?.content ?? part.smile?.name ?? "")
    .join(" ");
}

function handleChatMessage(
  message: any,
  eventbus: EventBus,
  emotesStore: EmotesStore,
): void {
  log("DEBUG", { message }, "VK Live chat message");
  const emotes = emotesFromText(messageText(message), emotesStore);
  emotes.push(
    ...(message.parts ?? [])
      .filter((part: any) => part.smile)
      .map((part: any) => {
        return {
          type: "vklive",
          name: part.smile.name,
          id: part.smile.id,
          gif: part.smile.animated,
          urls: {
            "1": part.smile.medium_url,
            "2": part.smile.medium_url,
            "4": part.smile.medium_url,
          },
          start: 0,
          end: 0,
        };
      }),
  );
  const badges = (message.author?.badges ?? []).map(
    (badge: {
      achievement_name: string;
      id: string;
      large_url: string;
      name: string;
    }) => ({
      type: badge.achievement_name,
      version: badge.id,
      url: badge.large_url,
      description: badge.name || badge.achievement_name,
    }),
  );
  const variables: Variable[] = [
    {
      id: uuidv7(),
      name: "chatter_user_login",
      value: message.author?.nick ?? "",
      type: "string",
    },
    {
      id: uuidv7(),
      name: "chatter_color",
      value: "#FFFFFF",
      type: "string",
    },
    {
      id: uuidv7(),
      name: "message_text",
      value: messageText(message),
      type: "string",
    },
    {
      id: uuidv7(),
      name: "message_id",
      value: String(message.id ?? ""),
      type: "string",
    },
    {
      id: uuidv7(),
      name: "emotes",
      value: emotes,
      type: "object",
    },
    {
      id: uuidv7(),
      name: "badges",
      value: badges,
      type: "object",
    },
  ];
  eventbus.push(new Event(EVENT_NAME, variables));
}

function handleChatData(
  data: any,
  eventbus: EventBus,
  emotesStore: EmotesStore,
): void {
  log("INFO", { data }, "VKLive chat data");
  const message = data?.data?.chat_message;
  log("INFO", { message }, "VK Live chat message");
  if (!message) return;
  handleChatMessage(message, eventbus, emotesStore);
}

function handleFrame(
  frame: any,
  channel: string,
  subscriptionToken: string,
  websocketClient: WebSocket,
  eventbus: EventBus,
  emotesStore: EmotesStore,
): void {
  log("INFO", { frame }, "VK Live WebSocket frame");
  if (frame.id === 1 && frame.connect) {
    websocketClient.send(
      JSON.stringify({
        id: 2,
        subscribe: { channel, token: subscriptionToken },
      }),
    );
  } else if (frame.id === 2 && frame.subscribe) {
    log("INFO", `Subscribed to VK Live channel [${channel}]`);
  } else if (frame.push) {
    handleChatData(frame.push.pub?.data, eventbus, emotesStore);
  } else if (Object.keys(frame).length === 0) {
    websocketClient.send("{}"); // pong
  }
}

function startWebSocketClient(
  odaToken: string,
  channel: string,
  connectionToken: string,
  subscriptionToken: string,
  eventbus: EventBus,
  emotesStore: EmotesStore,
): WebSocket {
  log("INFO", { channel }, "Starting VK Live WebSocket connection");
  const websocketClient = new WebSocket(VKLIVE_WEBSOCKET_URL);
  let reconnecting = false;
  websocketClients.add(websocketClient);

  // Reconnect on abnormal close/error. Guarded so error + close firing
  // together only schedule one reconnect. Re-fetch tokens via
  // startVKLiveClient since the connection token is short-lived.
  const scheduleReconnect = (): void => {
    if (reconnecting) return;
    reconnecting = true;
    setTimeout(() => {
      startVKLiveClient(odaToken, eventbus, emotesStore);
    }, RECONNECT_DELAY_MS);
  };

  websocketClient.addEventListener("error", (err) => {
    log("ERROR", "VK Live WebSocket error:", err);
    reportError(odaToken, "VKLive", `WebSocket error: ${err}`);
    scheduleReconnect();
  });

  websocketClient.addEventListener("open", () => {
    log("INFO", "WebSocket connection opened to " + VKLIVE_WEBSOCKET_URL);
    reportStarted(odaToken, "VKLive");
    websocketClient.send(
      JSON.stringify({ id: 1, connect: { token: connectionToken } }),
    );
  });

  websocketClient.addEventListener("close", (event) => {
    const wasRegistered = websocketClients.delete(websocketClient);
    if (event.code === 1000) return;
    reportError(
      odaToken,
      "VKLive",
      `WebSocket closed with code ${event.code}${event.reason ? `: ${event.reason}` : ""}`,
    );
    if (!wasRegistered) return; // Closed by deregister — do not reconnect.
    log("INFO",
      `VK Live WebSocket closed. Reconnection attempt in ${RECONNECT_DELAY_MS}ms`,
    );
    scheduleReconnect();
  });

  websocketClient.addEventListener("message", (data) => {
    const frames = (data.data as string)
      .split("\n")
      .filter((frame) => frame.trim() !== "");
    frames.forEach((frame) =>
      handleFrame(
        JSON.parse(frame),
        channel,
        subscriptionToken,
        websocketClient,
        eventbus,
        emotesStore,
      ),
    );
  });

  return websocketClient;
}

async function startVKLiveClient(
  token: string,
  eventbus: EventBus,
  emotesStore: EmotesStore,
) {
  try {
    const channel = await getChatChannelName(token);
    const [connectionToken, subscriptionToken] = await Promise.all([
      getConnectionToken(token),
      getSubscriptionToken(token, channel),
    ]);
    startWebSocketClient(
      token,
      channel,
      connectionToken,
      subscriptionToken,
      eventbus,
      emotesStore,
    );
  } catch (error) {
    log("ERROR", "Failed to start VK Live chat client", error);
    reportError(token, "VKLive", `failed to start chat client: ${error}`);
  }
}

// Endpoint comes from the build-time environment, with a fallback to the
// production gateway (same pattern as the chat/donation handlers).
const RECIPIENT_API_ENDPOINT =
  process.env.REACT_APP_RECIPIENT_API_ENDPOINT ?? "https://api.oda.digital";

let connectedTokens: string[] = [];
const websocketClients = new Set<WebSocket>();

export function register(
  odaToken: string,
  recipientId: string,
  eventbus: EventBus,
  emotesStore: EmotesStore,
  tokens: TokenDto[] | null,
): void {
  if (!tokens) {
    reportError(odaToken, "VKLive", "Failed to fetch recipient tokens");
    return;
  }
  const auth = { headers: { Authorization: `Bearer ${odaToken}` } };
  tokens
    .filter((token) => token.system === "VKLive")
    .filter((token) => !connectedTokens.includes(token.id))
    .forEach((token) => {
      log("INFO", `add handler for ${token.id}`);
      connectedTokens.push(token.id);
      getAccessToken({
        body: { tokenId: token.id },
        headers: auth.headers,
        baseURL: RECIPIENT_API_ENDPOINT,
        throwOnError: true,
      })
        .then((response) =>
          startVKLiveClient(response.data.token, eventbus, emotesStore),
        )
        .catch((err) => {
          reportError(odaToken, "VKLive", String(err));
        });
    });
}

export function deregister(): void {
  log("INFO", { connected: connectedTokens }, "remove vklive-listener");
  websocketClients.forEach((websocketClient) => {
    websocketClient.close(1000, "deregistered");
    websocketClients.delete(websocketClient);
  });
  connectedTokens = [];
}
